import { Box, Typography, Paper } from '@gapartners/ui';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { SentimentProgressionPoint } from '@/types/qualityMetrics';

interface SentimentTrendProps {
  progression: SentimentProgressionPoint[];
  direction?: 'improving' | 'declining' | 'stable';
  title?: string;
  height?: number;
}

export function SentimentTrend({
  progression,
  direction = 'stable',
  title = 'Sentiment Progression',
  height = 300,
}: SentimentTrendProps) {
  const { t } = useTranslation();

  if (!progression || progression.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={height - 60}
        >
          <Typography color="text.secondary">
            {t(
              'pages.manager.qualityMetrics.call.components.noProgressionData'
            )}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Transform data for Recharts - each point has positive, neutral, negative percentages
  const chartData = progression.map((item, idx) => ({
    quarter: `Q${idx + 1}`,
    positive: item.positive,
    neutral: item.neutral,
    negative: item.negative,
  }));

  // Get direction label
  const directionLabel = {
    improving: t('pages.manager.qualityMetrics.call.components.improving'),
    declining: t('pages.manager.qualityMetrics.call.components.declining'),
    stable: t('pages.manager.qualityMetrics.call.components.stable'),
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, height }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('pages.manager.qualityMetrics.call.components.trend')}:
          </Typography>
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              color:
                direction === 'improving'
                  ? '#4CAF50'
                  : direction === 'declining'
                    ? '#F44336'
                    : '#FFC107',
            }}
          >
            {directionLabel[direction]}
          </Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={height - 100}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="quarter"
            label={{
              value: t(
                'pages.manager.qualityMetrics.call.components.progressionIndex'
              ),
              position: 'insideBottomRight',
              offset: -5,
            }}
          />
          <YAxis
            domain={[0, 100]}
            label={{
              value: '%',
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip
            formatter={(value: number) => `${value.toFixed(1)}%`}
            labelFormatter={label => `${label}`}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="positive"
            stroke="#4CAF50"
            name={t('pages.manager.qualityMetrics.call.components.positive')}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="neutral"
            stroke="#FFC107"
            name={t('pages.manager.qualityMetrics.call.components.neutral')}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="negative"
            stroke="#F44336"
            name={t('pages.manager.qualityMetrics.call.components.negative')}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}
